import { zodResolver } from '@hookform/resolvers/zod';
import { Box, InputAdornment } from '@mui/material';
import { CalendarIcon, MobileDatePicker } from '@mui/x-date-pickers';
import { addDays, sub } from 'date-fns';
import { saveAs } from 'file-saver';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { lightText } from 'src/colors';
import { InputField } from 'src/components/atoms/input-field';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { useExportBookingsAsXls } from 'src/features/bookings/api/hooks/use-export-bookings-as-xls';
import { z } from 'zod';
import { useExportBookingsAsXlsDialogFormValidationSchema } from './schema/use-form-validation-schema';

interface Props extends DialogActions {}

export const ExportBookingsAsXlsDialog = ({ open, onCancel }: Props) => {
  const { t } = useTranslation(['bookings', 'common']);

  const formValidationSchema =
    useExportBookingsAsXlsDialogFormValidationSchema();

  type FormValues = z.infer<typeof formValidationSchema>;

  const twoMonthsAgo = sub(new Date(), { days: 60 });

  const defaultValues: FormValues = {
    from: twoMonthsAgo,
    to: new Date(),
  };

  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    resolver: zodResolver(formValidationSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const handleOnCancel = () => {
    onCancel?.();
    reset(defaultValues);
  };

  const {
    mutateAsync: exportBookingsAsXls,
    isPending: isExportingBookingsAsXls,
  } = useExportBookingsAsXls(handleOnCancel);

  const handleExportBookingsAsXls = async (data: FormValues) => {
    const { content, fileName } = await exportBookingsAsXls({
      from: data.from,
      to: data.to,
    });

    saveAs(content, fileName);
  };

  const from = watch('from');

  return (
    <Dialog
      open={open}
      title={t('bookings:common.exportBookingsAsXlsDialog.title')}
      onCancel={handleOnCancel}
      onConfirm={handleSubmit(handleExportBookingsAsXls)}
      confirmButtonText={t(
        'bookings:common.exportBookingsAsXlsDialog.download',
      )}
      cancelButtonText={t('common:button.cancel')}
      isConfirmButtonSubmitting={isExportingBookingsAsXls}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 2,
        }}>
        <Controller
          control={control}
          name="from"
          render={({
            field: { onChange, value, onBlur, name, ref },
            fieldState: { error },
          }) => (
            <MobileDatePicker
              value={value}
              format="dd/MM/yyyy"
              onChange={onChange}
              minDate={twoMonthsAgo}
              maxDate={new Date()}
              slots={{
                textField: props => (
                  <InputField
                    {...props}
                    ref={ref}
                    onBlur={onBlur}
                    name={name}
                    required
                    label={t('bookings:common.exportBookingsAsXlsDialog.from')}
                    error={!!error}
                    helperText={error?.message}
                    fullWidth
                  />
                ),
              }}
              slotProps={{
                textField: {
                  InputProps: {
                    endAdornment: (
                      <InputAdornment position="end" sx={{ mr: 2 }}>
                        <CalendarIcon sx={{ color: lightText.secondary }} />
                      </InputAdornment>
                    ),
                  },
                },
              }}
            />
          )}
        />

        <Box
          sx={{
            height: 60,
            display: 'flex',
            alignItems: 'flex-end',
          }}>
          <Box
            sx={{
              height: 34,
            }}>
            {' - '}
          </Box>
        </Box>

        <Controller
          control={control}
          name="to"
          render={({
            field: { onChange, value, onBlur, name, ref },
            fieldState: { error },
          }) => (
            <MobileDatePicker
              value={value}
              format="dd/MM/yyyy"
              onChange={onChange}
              minDate={addDays(from, 1)}
              maxDate={new Date()}
              slots={{
                textField: props => (
                  <InputField
                    {...props}
                    ref={ref}
                    onBlur={onBlur}
                    name={name}
                    required
                    label={t('bookings:common.exportBookingsAsXlsDialog.to')}
                    error={!!error}
                    helperText={error?.message}
                    fullWidth
                  />
                ),
              }}
              slotProps={{
                textField: {
                  InputProps: {
                    endAdornment: (
                      <InputAdornment position="end" sx={{ mr: 2 }}>
                        <CalendarIcon sx={{ color: lightText.secondary }} />
                      </InputAdornment>
                    ),
                  },
                },
              }}
            />
          )}
        />
      </Box>
    </Dialog>
  );
};
